import axios from "axios";
import axiosInstance from "utils/axios";

/**
 * Entity Types Enum matching backend DocumentEntityType
 * Lead = 1, Job = 2, Global/Settings = 3
 */
export const DOCUMENT_ENTITY_TYPES = {
  LEAD: 1,
  JOB: 2,
  GLOBAL: 3,
  SETTINGS: 3,
};

/**
 * Helper to reliably determine if backend API response indicates success or failure.
 */
const checkApiSuccess = (resData) => {
  if (!resData || typeof resData !== "object") return false;
  if (resData.success === false) return false;
  if (resData.statusCode && resData.statusCode !== 200) return false;
  if (
    resData.status === "Failure" ||
    resData.status === "Error" ||
    resData.status === "Failed"
  ) {
    return false;
  }
  return true;
};

/**
 * Fetches folder contents (subfolders and documents) for a given entity & optional folderId.
 * POST /api/DocumentFolders/GetFolders
 */
export const getDocumentFolders = async ({
  entityType = DOCUMENT_ENTITY_TYPES.LEAD,
  entityId,
  leadId,
  folderId = null,
  fileType = null,
  search = "",
  startDate = null,
  endDate = null,
  modifiedStartDate = null,
  modifiedEndDate = null,
  modifiedStart = null,
  modifiedEnd = null,
  pageNumber = 1,
  pageSize = 10,
}) => {
  try {
    const targetEntityId = entityId || leadId;
    const response = await axiosInstance.post("/api/DocumentFolders/GetFolders", {
      entityType,
      entityId: targetEntityId,
      folderId,
      fileType,
      search,
      uploadStart: startDate,
      uploadEnd: endDate,
      uploadStartDate: startDate,
      uploadEndDate: endDate,
      modifiedStart: modifiedStartDate || modifiedStart,
      modifiedEnd: modifiedEndDate || modifiedEnd,
      modifiedStartDate: modifiedStartDate || modifiedStart,
      modifiedEndDate: modifiedEndDate || modifiedEnd,
      pageNumber,
      pageSize,
    });

    const resData = response.data;
    const isSuccess = checkApiSuccess(resData);

    if (isSuccess) {
      let payload = resData.data;
      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch (e) {
          payload = resData;
        }
      }
      if (!payload || typeof payload !== "object") {
        payload = resData;
      }

      const rawDocuments =
        payload.files ||
        payload.Files ||
        payload.documents ||
        payload.Documents ||
        (Array.isArray(payload) ? payload : []);
      const rawFolders = payload.folders || payload.Folders || [];

      const transformedDocuments = rawDocuments.map((doc) => ({
        id: doc.DocumentId || doc.id,
        DocumentId: doc.DocumentId || doc.id,
        name: doc.FileName || doc.name,
        FileName: doc.FileName || doc.name,
        fileType: (doc.FileName || doc.name || "").split(".").pop().toLowerCase(),
        size: (doc.FileSize || doc.size || 0) * (doc.FileSize > 1000 ? 1 : 1024),
        UploadedByName: doc.UploadedByName || doc.uploadedByName || doc.CreatedByName || doc.createdByName || "System",
        UploadedOn: doc.UploadedOn || doc.uploadedOn || doc.CreatedOn || null,
        ModifiedByName: doc.ModifiedByName || doc.modifiedByName || null,
        ModifiedOn: doc.ModifiedOn || doc.modifiedOn || null,
        leadId: targetEntityId,
        filePath: doc.FilePath || doc.filePath,
        FilePath: doc.FilePath || doc.filePath,
        isVerified: doc.IsVerified ?? doc.IsVerfied ?? false,
        docVerificationId: doc.DocVerificationId,
        canApproveOrReject: doc.CanApproveOrReject,
        verificationStatus:
          typeof doc.VerificationStatus === "number"
            ? doc.VerificationStatus
            : parseInt(doc.VerificationStatus ?? 0, 10) || 0,
        verifiedBy: doc.VerifiedByName || doc.VerifiedBy || doc.VerifiedById || null,
        verifiedAt: doc.VerifiedAt || doc.VerifiedDate || null,
        verificationRemark: doc.Remark || doc.Remarks || doc.VerificationRemark || "",
        isHistoryItem: false,
        folderId: doc.FolderId ?? doc.folderId ?? null,
        FolderId: doc.FolderId ?? doc.folderId ?? null,
      }));

      const transformedFolders = rawFolders.map((f) => ({
        FolderId: f.FolderId || f.folderId || f.id,
        id: f.FolderId || f.folderId || f.id,
        Name: f.Name || f.name,
        name: f.Name || f.name,
        CreatedOn: f.CreatedOn || f.createdOn || null,
        UploadedByName: f.UploadedByName || f.uploadedByName || f.CreatedByName || f.createdByName || "System",
        ModifiedOn: f.ModifiedOn || f.modifiedOn || null,
        ModifiedByName: f.ModifiedByName || f.modifiedByName || null,
        ParentFolderId: f.ParentFolderId || f.parentFolderId || null,
        IsDefault: f.IsDefault ?? f.isDefault ?? false,
      }));

      const resolvedCurrentFolderId =
        resData.currentFolderId ||
        resData.CurrentFolderId ||
        payload.currentFolderId ||
        payload.CurrentFolderId ||
        payload.folderId ||
        payload.FolderId ||
        payload.rootFolderId ||
        payload.RootFolderId ||
        folderId;

      const resolvedParentFolderId =
        resData.parentFolderId ||
        resData.ParentFolderId ||
        payload.parentFolderId ||
        payload.ParentFolderId ||
        null;

      return {
        success: true,
        data: transformedDocuments,
        folders: transformedFolders,
        currentFolderId: resolvedCurrentFolderId,
        parentFolderId: resolvedParentFolderId,
        currentFolderName:
          payload.currentFolderName ||
          payload.CurrentFolderName ||
          resData.currentFolderName ||
          "",
        totalRecords: resData.totalRecords ?? payload.totalRecords ?? transformedDocuments.length,
      };
    } else {
      return {
        success: false,
        error: resData.message || resData.error || "Failed to fetch folders",
        data: [],
        folders: [],
        currentFolderId: null,
        parentFolderId: null,
        totalRecords: 0,
      };
    }
  } catch (error) {
    console.error("Error in getDocumentFolders:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Error fetching document folders",
      data: [],
      folders: [],
      currentFolderId: null,
      parentFolderId: null,
      totalRecords: 0,
    };
  }
};

/**
 * Creates a new document folder under a parent folder.
 * POST /api/DocumentFolders/CreateFolder
 */
export const createDocumentFolder = async ({
  entityType = DOCUMENT_ENTITY_TYPES.LEAD,
  entityId,
  leadId,
  parentFolderId = null,
  name,
}) => {
  try {
    const targetEntityId = entityId || leadId;
    const response = await axiosInstance.post("/api/DocumentFolders/CreateFolder", {
      entityType,
      entityId: targetEntityId,
      parentFolderId,
      name: name.trim(),
    });

    const resData = response.data;
    if (checkApiSuccess(resData)) {
      return {
        success: true,
        message: resData.message || "Folder created successfully",
        data: resData.data,
      };
    } else {
      return {
        success: false,
        error: resData.message || resData.error || "Failed to create folder",
      };
    }
  } catch (error) {
    console.error("Error creating folder:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to create folder",
    };
  }
};

/**
 * Renames an existing folder.
 * POST /api/DocumentFolders/RenameFolder
 */
export const renameDocumentFolder = async ({ folderId, name }) => {
  try {
    const response = await axiosInstance.post("/api/DocumentFolders/RenameFolder", {
      folderId,
      name: name.trim(),
    });

    const resData = response.data;
    if (checkApiSuccess(resData)) {
      return {
        success: true,
        message: resData.message || "Folder renamed successfully",
      };
    } else {
      return {
        success: false,
        error: resData.message || resData.error || "Failed to rename folder",
      };
    }
  } catch (error) {
    console.error("Error renaming folder:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to rename folder",
    };
  }
};

/**
 * Recursively soft-deletes a folder and its contents.
 * POST /api/DocumentFolders/DeleteFolder
 */
export const deleteDocumentFolder = async ({ folderId, newDefaultFolderId = null }) => {
  try {
    const response = await axiosInstance.post("/api/DocumentFolders/DeleteFolder", {
      folderId,
      newDefaultFolderId,
    });

    const resData = response.data;
    if (checkApiSuccess(resData)) {
      return {
        success: true,
        message: resData.message || "Folder deleted successfully",
      };
    } else {
      return {
        success: false,
        error: resData.message || resData.error || "Failed to delete folder",
      };
    }
  } catch (error) {
    console.error("Error deleting folder:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to delete folder",
    };
  }
};

/**
 * Marks a folder as the default upload target for its Lead/Job.
 * POST /api/DocumentFolders/SetDefaultFolder
 */
export const setDefaultDocumentFolder = async ({ folderId }) => {
  try {
    const response = await axiosInstance.post("/api/DocumentFolders/SetDefaultFolder", {
      folderId,
    });

    const resData = response.data;
    if (checkApiSuccess(resData)) {
      return {
        success: true,
        message: resData.message || "Default folder updated successfully",
        data: resData.data,
      };
    } else {
      return {
        success: false,
        error: resData.message || resData.error || "Failed to set default folder",
      };
    }
  } catch (error) {
    console.error("Error setting default folder:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to set default folder",
    };
  }
};

/**
 * Renames an existing document/file.
 * POST /api/DocumentFolders/RenameFile
 */
export const renameDocument = async ({ documentId, id, fileName, name }) => {
  try {
    const targetDocId = documentId || id;
    const targetName = fileName || name;
    const response = await axiosInstance.post("/api/DocumentFolders/RenameFile", {
      documentId: targetDocId,
      Name: targetName.trim(),
    });

    const resData = response.data;
    if (checkApiSuccess(resData)) {
      return {
        success: true,
        message: resData.message || "Document renamed successfully",
        data: resData.data,
      };
    } else {
      return {
        success: false,
        error: resData.message || resData.error || "Failed to rename document",
      };
    }
  } catch (error) {
    console.error("Error renaming document:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to rename document",
    };
  }
};

/**
 * Moves a folder or file to a destination folder.
 * POST /api/DocumentFolders/MoveItem
 */
export const moveDocumentItem = async ({ id, destinationFolderId, isFolder = false }) => {
  try {
    const response = await axiosInstance.post("/api/DocumentFolders/MoveItem", {
      id,
      destinationFolderId,
      isFolder: Boolean(isFolder),
      folderId: isFolder ? id : undefined,
      documentId: !isFolder ? id : undefined,
    });

    const resData = response.data;
    if (checkApiSuccess(resData)) {
      return {
        success: true,
        message: resData.message || "Item moved successfully",
        data: resData.data,
      };
    } else {
      return {
        success: false,
        error: resData.message || resData.error || "Failed to move item",
      };
    }
  } catch (error) {
    console.error("Error moving item:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to move item",
    };
  }
};

/**
 * Moves multiple folders or files to a destination folder.
 * POST /api/DocumentFolders/MoveItems
 */
export const moveDocumentItems = async ({ ids = [], destinationFolderId }) => {
  try {
    const response = await axiosInstance.post("/api/DocumentFolders/MoveItems", {
      ids,
      destinationFolderId,
    });

    const resData = response.data;
    if (checkApiSuccess(resData)) {
      return {
        success: true,
        message: resData.message || "Items moved successfully",
        data: resData.data,
      };
    } else {
      return {
        success: false,
        error: resData.message || resData.error || "Failed to move items",
      };
    }
  } catch (error) {
    console.error("Error moving items:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to move items",
    };
  }
};


/**
 * Uploads raw binary files to S3 via presigned URLs for a folder upload,
 * returning the S3 file keys without committing rows to DocumentFolders yet.
 */
export const uploadFolderRawFilesToS3 = async ({
  entityType = DOCUMENT_ENTITY_TYPES.LEAD,
  entityId,
  leadId,
  parentFolderId = null,
  files = [],
}) => {
  const targetEntityId = entityId || leadId;
  const fileMetas = files.map((file) => ({
    fileName: file.name,
    contentType: file.type || "application/octet-stream",
    fileSizeBytes: file.size,
  }));

  const isJob = entityType === DOCUMENT_ENTITY_TYPES.JOB;
  const initiateUrl = isJob
    ? "/api/Jobs/InitiateJobDocumentUpload"
    : "/api/Leads/InitiateLeadDocumentUpload";

  const payload = isJob
    ? { jobId: targetEntityId, folderId: parentFolderId, files: fileMetas }
    : { leadId: targetEntityId, folderId: parentFolderId, files: fileMetas };

  const initiateResponse = await axiosInstance.post(initiateUrl, payload);
  const presignedItems = initiateResponse.data?.data || [];

  if (presignedItems.length !== files.length) {
    throw new Error("Upload initialization failed: mismatched file count.");
  }

  await Promise.all(
    files.map((file, index) =>
      axios.put(presignedItems[index].uploadUrl, file, {
        headers: { "Content-Type": file.type || "application/octet-stream" },
      })
    )
  );

  return presignedItems;
};

/**
 * Uploads a complete folder structure (relative path items + folder nodes).
 * POST /api/DocumentFolders/UploadFolder
 */
export const uploadDocumentFolderStructure = async ({
  entityType = DOCUMENT_ENTITY_TYPES.LEAD,
  entityId,
  leadId,
  parentFolderId = null,
  files = [],
}) => {
  try {
    const targetEntityId = entityId || leadId;
    const response = await axiosInstance.post("/api/DocumentFolders/UploadFolder", {
      entityType,
      entityId: targetEntityId,
      parentFolderId,
      files,
    });

    const resData = response.data;
    if (checkApiSuccess(resData)) {
      return {
        success: true,
        message: resData.message || "Folder structure uploaded successfully",
        data: resData.data,
      };
    } else {
      return {
        success: false,
        error: resData.message || resData.error || "Failed to upload folder structure",
      };
    }
  } catch (error) {
    console.error("Error uploading folder structure:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to upload folder structure",
    };
  }
};

/**
 * Downloads a folder and all its contents as a ZIP archive.
 * GET /api/DocumentFolders/DownloadFolder?folderId={folderId}
 */
export const downloadDocumentFolderZip = async (folderId, folderName = "Folder") => {
  try {
    const response = await axiosInstance.get("/api/DocumentFolders/DownloadFolder", {
      params: { folderId },
      responseType: "blob",
    });

    const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));
    const link = document.createElement("a");
    link.href = url;
    const safeName = (folderName || "Folder").replace(/[^a-zA-Z0-9_\-]/g, "_");
    link.setAttribute("download", `${safeName}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return {
      success: true,
      message: "Folder ZIP download started.",
    };
  } catch (error) {
    console.error("Error downloading folder ZIP:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to download folder as ZIP",
    };
  }
};
